/**
 * "Ask about this": words someone selects in a finished answer, sent beside
 * their question as its own field (functions/chat.js). The quote never goes
 * inside what they typed, so the crisis screen and the safety queue keep
 * reading only their own words.
 *
 *   quote  { text, from: 'answer' | 'approved' | 'book', chapter? }
 *          500 characters at most, 200 from the book; `chapter` only for the
 *          book, a whole number from 0 (the Introduction) to 20
 *
 * The first part is plain functions with no imports and no page, so
 * scripts/check-ask-about.js runs exactly this code, and runs the same table
 * through the server's copy (functions/guards.js) so the two can't drift.
 * No regex lookbehind anywhere here: Safari before 16.4 refuses a whole
 * script that has one, and the app would be blank on older iPhones.
 *
 * The switches live in meta/publicConfig (scripts/set-public-config.js):
 * askAbout 1 shows the offer to everyone, and a browser marked as the team's
 * (?apr_internal=1) sees it either way; askAboutBook needs both, and stays 0
 * until Alexi says yes.
 */

export const MAX_QUOTE_CHARS = 500;
export const MAX_BOOK_QUOTE_CHARS = 200;
export const QUOTE_FROM = ['answer', 'approved', 'book'];
export const ASK_ABOUT_DEFAULTS = { askAbout: 0, askAboutBook: 0 };

// The marks that can wrap a whole quote, opening and closing.
const QUOTE_PAIRS = [['"', '"'], ['“', '”'], ['„', '“'], ['«', '»'], ['‘', '’'], ["'", "'"]];
// The book's chapters: the Introduction (0) to Chapter Twenty.
const MAX_QUOTE_CHAPTER = 20;
const CHAPTER_WORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen', 'Twenty'];
const INTERNAL_KEY = 'apr-internal'; // a browser marked as the team's (utils/analytics.js), as utils/giving.js reads it
// What a selection's edges can leave stray: , ; : ) at the start, , ; : ( at
// the end; and what may not dangle before a cut quote's "…".
const STRAY_START = /[,;:)\s]/;
const STRAY_END = /[,;:(\s]/;
const DANGLING = /[\s,;:(–—-]/;

/**
 * Where `text` ends once every character `set` matches is gone from its end,
 * a character at a time: a regex anchored at the end ([…]+$) tries again from
 * every place in a long run of them (the server's copy, functions/guards.js).
 */
function endWithout(text, set, start = 0) {
  let end = text.length;
  while (end > start && set.test(text.charAt(end - 1))) end -= 1;
  return end;
}

/** A stray , ; : or ) left at the start of a selection, and , ; : or ( at its end, gone. */
function stripStrayPunctuation(text) {
  let start = 0;
  while (start < text.length && STRAY_START.test(text.charAt(start))) start += 1;
  return text.slice(start, endWithout(text, STRAY_END, start));
}

/** One pair of quotation marks around the whole, gone (never a pair that only looks like one). */
function unwrapQuote(text) {
  for (let i = 0; i < QUOTE_PAIRS.length; i++) {
    const [open, close] = QUOTE_PAIRS[i];
    if (text.length > 2 && text.startsWith(open) && text.endsWith(close)) {
      const inner = text.slice(open.length, text.length - close.length);
      if (!inner.includes(open) && !inner.includes(close)) return inner.trim();
    }
  }
  return text;
}

/** Cut to `max` characters at a word boundary, the "…" counted inside. */
function cutQuoteAtWord(text, max) {
  let cut = text.slice(0, max - 1);
  if (/[\uD800-\uDBFF]$/.test(cut)) cut = cut.slice(0, -1); // never half a character
  if (!/\s/.test(text.charAt(cut.length))) {
    const space = cut.search(/\s\S*$/);
    if (space > max / 2) cut = cut.slice(0, space);
  }
  return `${cut.slice(0, endWithout(cut, DANGLING))}…`;
}

/**
 * Words quoted from an answer, made plain: NFC; control, zero-width and bidi
 * characters gone, line breaks kept; runs of space made one, at most one
 * blank line; stray punctuation at the edges and one pair of wrapping
 * quotation marks gone; then cut at a word boundary to `max` characters, the
 * "…" counted inside. null when fewer than two letters or digits are left.
 * The same steps as the server's (functions/guards.js normalizeQuote).
 */
export function normalizeQuote(raw, max = MAX_QUOTE_CHARS) {
  if (typeof raw !== 'string') return null;
  let text = raw.slice(0, max * 4).normalize('NFC')
    .replace(/\r\n?|[\u{85}\u{2028}\u{2029}]/gu, '\n')
    .replace(/[\t\v\f]/g, ' ')
    .replace(/(?!\n)[\p{Cc}\p{Cf}\p{Cs}]/gu, '')
    .replace(/[^\S\n]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  text = stripStrayPunctuation(unwrapQuote(stripStrayPunctuation(text)));
  if (text.length > max) text = cutQuoteAtWord(text, max);
  const letters = text.match(/[\p{L}\p{N}]/gu);
  return letters && letters.length >= 2 ? text : null;
}

/**
 * A quote as the request may carry it, { text, from, chapter? }, or null: the
 * server's rules (functions/guards.js cleanQuote). A plain object only; a
 * quote from the book needs its chapter, and no other kind keeps one; any
 * other key is dropped. Never throws.
 */
export function cleanQuote(raw) {
  try {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const proto = Object.getPrototypeOf(raw);
    if (proto !== Object.prototype && proto !== null) return null;
    const from = QUOTE_FROM.includes(raw.from) ? raw.from : null;
    if (!from) return null;
    const book = from === 'book';
    if (book && !(Number.isInteger(raw.chapter) && raw.chapter >= 0 && raw.chapter <= MAX_QUOTE_CHAPTER)) return null;
    const text = normalizeQuote(raw.text, book ? MAX_BOOK_QUOTE_CHARS : MAX_QUOTE_CHARS);
    if (!text) return null;
    return book ? { text, from, chapter: raw.chapter } : { text, from };
  } catch (e) {
    return null;
  }
}

/** What an empty box sends with a quote. */
export function defaultAsk(quote) {
  return quote && quote.from === 'book' ? 'Tell me more about this part of Genius.' : 'Tell me more about this.';
}

/** "Chapter Eleven", or "Introduction" for 0. */
function chapterName(n) {
  return n === 0 ? 'Introduction' : `Chapter ${CHAPTER_WORDS[n] || n}`;
}

/** The words above a quote: "Quoting AI Rob", "Quoting Rob’s approved answer", "Quoting Genius · Chapter Ten". */
export function quoteLabel(quote) {
  if (quote && quote.from === 'approved') return 'Quoting Rob’s approved answer';
  if (quote && quote.from === 'book') return `Quoting Genius · ${chapterName(quote.chapter)}`;
  return 'Quoting AI Rob';
}

/**
 * A quoted question as a person reads it, where only words travel (the
 * feedback and "Ask the real Rob" the team reads): "Quoting AI Rob: “…”", a
 * blank line, then the question. Unquoted, the question as it was.
 */
export function foldForPeople(content, quote) {
  const q = cleanQuote(quote);
  const words = String(content || '');
  return q ? `${quoteLabel(q)}: “${q.text}”\n\n${words}` : words;
}

/** A switch from meta/publicConfig or an answer's metadata: 0 or 1, or null when missing or not a number. */
function switchValue(value) {
  let n = null;
  if (typeof value === 'number') n = Number.isFinite(value) ? value : null;
  else if (typeof value === 'string' && value.trim() !== '') n = Number.isFinite(Number(value.trim())) ? Number(value.trim()) : null;
  return n === null ? null : Math.min(1, Math.max(0, Math.floor(n)));
}

/**
 * The askAbout and askAboutBook fields in `source` that are there and are
 * numbers, pulled into 0 or 1 (the server's rule, functions/limits.js). A
 * missing or bad one is left out, so it never moves what the browser has.
 */
export function validAskAbout(source) {
  const s = source && typeof source === 'object' ? source : {};
  const out = {};
  Object.keys(ASK_ABOUT_DEFAULTS).forEach((key) => {
    const n = switchValue(s[key]);
    if (n !== null) out[key] = n;
  });
  return out;
}

/** Whether this browser is marked as the team's (?apr_internal=1); false when storage is blocked. */
export function teamBrowser() {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(INTERNAL_KEY) === '1';
  } catch (e) {
    return false;
  }
}

/** "Ask about this" for words in answers: the setting at 1, or a team browser whatever it says. */
export function askAboutOn(cfg, internal = teamBrowser()) {
  return Boolean(internal) || switchValue(cfg && cfg.askAbout) === 1;
}

/** The same for words in the Genius reader: askAbout's rule, and askAboutBook at 1 as well. */
export function askAboutBookOn(cfg, internal = teamBrowser()) {
  return askAboutOn(cfg, internal) && switchValue(cfg && cfg.askAboutBook) === 1;
}

// ---------------------------------------------------------------------------
// The page: what a selection quotes
// ---------------------------------------------------------------------------

// Where words can be quoted from: the body of a finished answer, never one
// still being written, a note, a person's turn, the sources or the reader.
export const QUOTE_SCOPE = '.cv-msg.assistant:not(.streaming):not(.local) .cv-markdown';
// Not the answer's words: citation numbers and what a screen reader hears for them.
const NOT_WORDS = 'sup.cv-cite, .cv-cite-sr, .apr-sr-only';
const BLOCK_TAGS = new Set(['P', 'LI', 'UL', 'OL', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'PRE', 'HR', 'DIV', 'TABLE', 'TR']);

function elementOf(node) {
  if (!node) return null;
  return node.nodeType === 1 ? node : node.parentElement;
}

/** The words of `node` inside `range`, citation numbers left out, a line break after each block. */
function collectText(node, range, out) {
  if (node.nodeType === 3) {
    if (!range.intersectsNode(node)) return;
    const from = node === range.startContainer ? range.startOffset : 0;
    const to = node === range.endContainer ? range.endOffset : node.data.length;
    out.push(node.data.slice(from, to));
    return;
  }
  if (node.nodeType !== 1 || node.matches(NOT_WORDS) || !range.intersectsNode(node)) return;
  if (node.tagName === 'BR') {
    out.push('\n');
    return;
  }
  for (let child = node.firstChild; child; child = child.nextSibling) collectText(child, range, out);
  // One break between blocks, however deep they nest (a list's last item ends the list too).
  if (BLOCK_TAGS.has(node.tagName) && out.length && !out[out.length - 1].endsWith('\n')) out.push('\n');
}

/**
 * The quote a selection makes, { text, from }, or null. Only inside `root`
 * (one conversation view: the archive and Focused Study stay mounted
 * together), and only from the one finished answer the selection starts in:
 * a drag on into the next question keeps that answer's words alone. A
 * citation's name and a link's words stay; the citation numbers go.
 */
export function quoteFromSelection(sel, root) {
  if (!sel || sel.isCollapsed || !sel.rangeCount || !root) return null;
  const range = sel.getRangeAt(0);
  const start = elementOf(range.startContainer);
  const answer = start && typeof start.closest === 'function' ? start.closest(QUOTE_SCOPE) : null;
  if (!answer || !root.contains(answer)) return null;
  const within = range.cloneRange();
  if (!answer.contains(range.endContainer)) within.setEnd(answer, answer.childNodes.length);
  const out = [];
  for (let child = answer.firstChild; child; child = child.nextSibling) collectText(child, within, out);
  const text = normalizeQuote(out.join(''), MAX_QUOTE_CHARS);
  if (!text) return null;
  return { text, from: answer.closest('.cv-msg.canonical') ? 'approved' : 'answer' };
}
